import React from "react";
import { useNavigate, useRouteError, isRouteErrorResponse } from "react-router-dom";
import { ERROR_CODES } from "@/config/error";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";

/**
 * ErrorFallback Component
 *
 * Displays a full-screen error UI with a glassmorphism design.
 * It shows an error code, title, message, and a navigation button.
 *
 * @param {string} [errorType="INTERNAL"] - Key to identify error type from ERROR_CODES
 * @param {string} [errorMessage=""] - Optional custom error message (currently unused)
 * @param {Function} [onRetry] - Optional retry handler function (currently unused)
 *
 * @returns {JSX.Element} Rendered error fallback UI
 */
const ErrorFallback = ({ errorType = "INTERNAL", errorMessage = "", onRetry }) => {
  const error = useRouteError();

  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Redirects user based on authentication state
  const onHandleClick = () => {
    if (onRetry) {
      onRetry?.();
    } else if (errorType !== "NOT_FOUND") {
      window.location.reload();
    } else {
      navigate(isAuthenticated ? "/admin/dashboard" : "/");
    }
  };

  /** Get error metadata based on error type */
  const errorData = ERROR_CODES[errorType];

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-tr from-[#1a2a1f] via-[#0b1220] to-[#0b3b5a] py-4">
      {/* Status Code + Glass Background */}
      <div className="relative flex flex-col items-center justify-center text-center px-6">
        {/* Large background error code */}
        <h1
          className={cn(
            "absolute font-bold text-text-base/70 whitespace-nowrap",
            typeof errorData.code === "number" ? "text-[180px] md:text-[260px]" : "text-[80px] md:text-[120px]",
          )}
        >
          {errorData.code}
        </h1>

        {/* Outer glow effect */}
        <div className="relative z-10 flex items-center justify-center">
          <div className="absolute w-72 h-72 rounded-full bg-text-base/10 blur-3xl opacity-40" />
        </div>

        {/* Glass orb container */}
        <div className="relative w-44 h-44 md:w-56 md:h-56 rounded-full bg-text-base/10 backdrop-blur-xl border border-text-base/20 shadow-[0_0_120px_rgba(255,255,255,0.25)_inset, 0_0_60px_rgba(255,255,255,0.15)] flex items-center justify-center">
          {/* Inner glow */}
          <div className="w-28 h-28 rounded-full bg-text-base/30 blur-2xl opacity-80" />

          {/* Center highlight */}
          <div className="absolute w-16 h-16 rounded-full bg-text-base/40 blur-xl" />
        </div>
      </div>

      {/* Error text content */}
      <div className="mt-12 z-10 text-center max-w-4xl">
        <h2 className="text-lg font-semibold text-text-base">{errorData.title}</h2>
        <p className="text-sm text-text-base/70 mt-2 whitespace-pre-line">
          {isRouteErrorResponse(error) ? error.statusText || error.data : error instanceof Error ? error.message : errorMessage || errorData.message}
        </p>
      </div>

      {/* Navigation button */}
      <Button
        className="mt-6 z-10 flex items-center gap-2 px-6 py-2.5 rounded-full bg-text-base/10 hover:bg-text-base/20 text-text-base/80 text-sm backdrop-blur-lg border border-text-base/20 shadow-md hover:shadow-lg transition-all duration-300"
        onClick={onHandleClick}
      >
        ← Back to Home
      </Button>
    </div>
  );
};

export default ErrorFallback;
